import { WEIGHT_UNIT } from '../types/enum';
import Resizer from 'react-image-file-resizer';

export const FORMAT = {
  DATE: 'YYYY-MM-DD',
  MONTH: 'YYYY-MM',
  MONTHDATE: 'MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  HOURMIN: 'HH:mm',
};

export const formatDate = (date, format) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (format === FORMAT.DATE) {
    return `${year}-${month}-${day}`;
  }
  if (format === FORMAT.MONTH) {
    return `${year}-${month}`;
  }
  if (format === FORMAT.MONTHDATE) {
    return `${month}-${day}`;
  }

  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  if (format === FORMAT.TIME) {
    return `${hours}:${minutes}:${seconds}`;
  }
  if (format === FORMAT.HOURMIN) {
    return `${hours}:${minutes}`;
  }
  if (format === FORMAT.DATETIME) {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  return `${year}-${month}-${day}`;
};

export const getDatesBetween = (startDate, endDate) => {
  let dates = [];
  let currentDate = new Date(startDate);

  // Ensure that the end date is included
  while (currentDate <= new Date(endDate)) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
  }

  return dates;
};

export const getWeeksBetween = (startDate, endDate) => {
  const weeks = [];
  let start = new Date(startDate);
  let end = new Date(endDate);

  // Ensure start is before end
  if (start > end) {
    [start, end] = [end, start]; // Swap if startDate is later than endDate
  }

  // Loop through weeks until we reach the end date
  while (start < end) {
    const weekStart = new Date(start);

    // Create a new end date for the week (add 6 days)
    const weekEnd = new Date(start);
    weekEnd.setDate(weekEnd.getDate() + 6);

    // If the calculated weekEnd is after the given end date, use the actual end date
    if (weekEnd > end) {
      weeks.push({
        week: weeks.length + 1,
        startDate: formatDate(weekStart),
        endDate: formatDate(end),
      });
      break;
    }

    weeks.push({
      week: weeks.length + 1,
      startDate: formatDate(weekStart),
      endDate: formatDate(weekEnd),
    });

    // Move start to the next week (7 days later)
    start.setDate(start.getDate() + 7);
  }

  return weeks;
};

export const groupDatesByYearAndMonth = (dates) => {
  const grouped = {};

  dates.forEach((date) => {
    const year = String(date.getFullYear());
    const month = String(date.getMonth() + 1).padStart(2, '0');

    if (!grouped[year]) {
      grouped[year] = {};
    }

    if (!grouped[year][month]) {
      grouped[year][month] = [];

      const firstDay = date.getDay();

      if (firstDay !== 0) {
        for (let i = 0; i < firstDay; i++) {
          grouped[year][month].push(null);
        }
      }
    }

    grouped[year][month].push(String(date.getDate()).padStart(2, '0'));
  });

  return grouped;
};

export const getMonthName = (month) => {
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  return months[month - 1];
};

export const getWeekName = (index) => {
  const weeks = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  return weeks[index];
};

export const getCurrentWeekDates = (date = new Date()) => {
  const result = [];
  const currentDate = new Date(date); // Create a new date object based on the parameter
  const dayOfWeek = currentDate.getDay(); // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)

  // Find the Monday of the current week
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(
    currentDate.setDate(currentDate.getDate() + mondayOffset)
  );

  // Populate the array with the week's dates (Monday to Sunday)
  for (let i = 0; i < 7; i++) {
    const weekDay = new Date(monday);
    weekDay.setDate(monday.getDate() + i);
    result.push(formatDate(weekDay));
  }

  return result;
};

export const resizeImgFile = (file) => {
  return new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      720,
      720,
      'jpg',
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'file',
      500,
      500
    );
  });
};

export const getLastDate = (offsetType) => {
  const today = new Date(); // Get today's date
  let lastDate = new Date(); // Initialize last date

  switch (offsetType) {
    case '30':
      lastDate.setDate(today.getDate() - 30); // Subtract 30 days
      break;
    case '90':
      lastDate.setDate(today.getDate() - 90); // Subtract 90 days
      break;
    case 'half':
      lastDate.setMonth(today.getMonth() - 6); // Subtract 6 months
      break;
    case 'year':
      lastDate.setFullYear(today.getFullYear() - 1); // Subtract 12 months
      break;
    default:
      throw new Error(
        'Invalid offsetType. Valid options are: 30, 90, half, year.'
      );
  }

  return lastDate;
};

export const getHoursFrom = (sec) => {
  return Math.floor(sec / 3600);
};

export const getRestMinutesFrom = (sec) => {
  return Math.floor((sec % 3600) / 60);
};

export const getRestSecondsFrom = (sec) => {
  return Math.floor(sec % 60);
};

export const convertWeight = (weight, unitFrom, unitTo) => {
  if (unitFrom === WEIGHT_UNIT.KG && unitTo === WEIGHT_UNIT.LB) {
    return +(+weight * 2.20462);
  }
  if (unitFrom === WEIGHT_UNIT.LB && unitTo === WEIGHT_UNIT.KG) {
    return +(+weight / 2.20462);
  }
  return weight;
};

export const isNumberOrEmpty = (value) => {
  return value === undefined || !isNaN(Number(value));
};
